import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API } from "../config";
import { useProfile } from "../context/ProfileContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faChevronRight, faClose, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as TelegramIcon } from "../telegram.svg";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { dark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { MarkdownA, MarkdownHeading1, MarkdownHeading2, MarkdownHeading3 } from "./MarkdownPage";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import ErrorBoundary from "./ErrorBoundary";

export function LoginPage() {
  const navigate = useNavigate();

  const { setToken } = useProfile();
  const [resetToken, setResetToken] = useState("");
  let searchPopupInitial = null;
  try {
    searchPopupInitial = localStorage.getItem("searchPopup");
  } catch (error) {
    console.error("Error reading searchPopup from localStorage:", error);
    searchPopupInitial = "true"; // Default to true if there's an error
  }
  const [searchPopup, setSearchPopup] = useState<boolean>(searchPopupInitial !== "false");
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<[{}]>([{}]);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const [helpPopup, setHelpPopup] = useState<boolean>(false);
  const [helpRefresh, setHelpRefresh] = useState<boolean>(false);
  const [requisiteRefresh, setRefreshRequisite] = useState<boolean>(false);
  const [telegramRefresh, setRefreshTelegram] = useState<boolean>(false);
  const [sourceMarkdownText, setSourceMarkdownText] = useState('');

  const [searchParams] = useSearchParams();
  const user = searchParams.get("user"); // returns "123" for ?user=123

  const setErrorMessage = (message: string) => {
    setError(message);
    setTimeout(() => {
      setError("");
    }, 5000);
  }

  const refreshDoc = (url: string) => {
    fetch(url)
      .then(res => res.json())
      .then((res) => {
        let announces: string = "";
        Object.entries(res?.public).reverse().map(([id, post]: [string, any]) => {
          // {id: string; title?: string; date?: string; body: string, enable?: boolean}
          if (post?.enable === true) {
            announces += "## " + (post?.title || "") + (post?.date ? `(${post?.date})` : "") + "\n";
            announces += (post?.body || "") + "\n";
          }
        });

        setSourceMarkdownText(announces);
      }).catch((error) => {
      setErrorMessage("Помилка завантаження допомоги");
      console.error("Error fetching help data:", error);
    });
  };

  useEffect(() => {
    if (!helpRefresh) return;
    refreshDoc(API.DOC_HELP);
  }, [helpRefresh]);

  useEffect(() => {
    if (!requisiteRefresh) return;
    refreshDoc(API.DOC_REQUISITE);
  }, [requisiteRefresh]);

  useEffect(() => {
    if (!telegramRefresh) return;
    refreshDoc(API.DOC_TELEGRAM);
  }, [telegramRefresh]);

  useEffect(() => {
    if (!helpPopup) {
      setSourceMarkdownText('');
    }
  }, [helpPopup]);

  useEffect(() => {
    try {
      localStorage.setItem("searchPopup", String(searchPopup));
    } catch (error) {
      setErrorMessage("Помилка збереження стану пошуку в localStorage");
      console.error("Error saving searchPopup state to localStorage:", error);
    }
  }, [searchPopup]);

  useEffect(() => {
    if (user) {
      setInput(user);
      setSearchPopup(false);
    } else {
      setInput("");
    }
  }, [user]);

  const handleLogin = async () => {
    try {
      const res = await fetch(API.LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ u: input, p: password }),
      });

      const data = await res.json();
      if (res.ok) {
        if (data.reset_password) {
          setResetToken(data.token);
        } else {
          try {
            localStorage.setItem("token", data.token);
          } catch (error) {
            setErrorMessage("Помилка збереження токена в localStorage");
            console.error("Error saving token to localStorage:", error);
          }
          setToken(data.token);
          setTimeout(() => {
            let page = "/message";
            try {
              page = localStorage.getItem("page") ?? "/message";
            } catch (error) {
              setErrorMessage("Помилка зщитування сторінки з localStorage");
              console.error("Error reading page from localStorage:", error);
            }
            navigate(page);
          }, 100);
        }
      } else {
        setErrorMessage(data.error || "Помилка входу");
      }
    } catch {
      setErrorMessage("Помилка мережі");
    }
  };

  const resetPassword = async () => {
    try {
      const res = await fetch(API.RESET_PASSWORD, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${resetToken}` },
        body: JSON.stringify({ p: password }),
      });

      const data = await res.json();
      if (res.ok) {
        if (data.updated) {
          setResetToken("");
        } else {
          setErrorMessage(data.error || "Помилка оновлення паролю");
        }
      } else {
        setErrorMessage(data.error || "Помилка оновлення паролю");
      }
    } catch {
      setErrorMessage("Помилка мережі");
    }
  };

  const findLogin = async (q: string) => {
    if (!q) {
      setFound([{}]);
      return;
    }

    try {
      const res = await fetch(`${API.LOGIN}?q=${q}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const data = await res.json();
      if (res.ok) {
        setFound(data || [{}]);
      } else {
        setFound([{}]);
      }
    } catch {
      setErrorMessage("Помилка мережі");
    }
  };

  useEffect(() => {
    findLogin(query);
  }, [query]);

  const submitOnEnter = (event: any) => {
    if (event.key === "Enter") {
      handleLogin();
    }
  };

  if (resetToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
          <h1 className="text-2xl font-bold mb-6 text-center">Становити новий паролю</h1>
          <p className="text-sm text-gray-600 mb-4">Введіть новий пароль для облікового запису:</p>
          <input
            className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            type="password"
            placeholder="Новий пароль"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                resetPassword();
              }
            }}
          />

          {error && <p className="text-sm text-red-500 mt-2">{error}</p>}

          <button
            className="mt-6 w-full bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-2 rounded-xl transition"
            onClick={resetPassword}
          >
            Скинути Пароль
          </button>

          <button
            className="mt-4 w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 rounded-xl transition"
            onClick={() => setResetToken("")}
          >Скасувати
          </button>
        </div>
      </div>
    );
  }

  const closeHelp = () => {
    setHelpRefresh(false);
    setRefreshRequisite(false);
    setRefreshTelegram(false);
    setHelpPopup(false);
  };

  const toggleHelp = () => {
    setHelpRefresh(prev => !prev);
    setHelpPopup(prev => !prev);
  };

  const toggleRequisite = () => {
    setRefreshRequisite(prev => !prev);
    setHelpPopup(prev => !prev);
  };

  const toggleTelegram = () => {
    setRefreshTelegram(prev => !prev);
    setHelpPopup(prev => !prev);
  };

  const someError = () => {
    throw new Error('Test error in paragraph rendering')
  }

  const RenderHelp = () => {
    try {
      return (
        <ErrorBoundary name={"LoginPage:RenderHelp"}>
          <Markdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex]}
            children={sourceMarkdownText}
            components={{
              h1: MarkdownHeading1,
              h2: MarkdownHeading2,
              h3: MarkdownHeading3,
              a: MarkdownA,
              p: ({ node, className, children, ...props }) => (
                <p className={`my-4 ${className || ''}`} {...props}>
                  {children}
                </p>
              ),
              blockquote: ({ node, className, children, ...props }) => (
                <blockquote className={`border-l-4 border-gray-300 pl-4 italic ${className || ''}`} {...props}>
                  {children}
                </blockquote>
              ),
              ul: ({ node, className, children, ...props }) => (
                <ul className={`list-decimal pl-6 my-4 ${className || ''}`} {...props}>
                  {children}
                </ul>
              ),
              code(props) {
                const { children, className, node, ref, ...rest } = props;
                return (
                  <SyntaxHighlighter
                    {...rest}
                    PreTag="pre"
                    style={dark}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                );
              }
            }}
          />
        </ErrorBoundary>
      )
    } catch (error) {
      return <div className="text-red-500">Error rendering help
        content: {error instanceof Error ? error.message : 'Unknown error'}</div>;
    }
  }

  if (helpPopup) {
    return (
      <div
        className={"min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4"}>
        <Card className="w-full max-w-xl mb-6 rounded-2xl shadow-lg text-left">
          <CardContent>
            <div className="flex items-center justify-between w-full">
              <div className="flex-none text-2xl font-semibold text-right">
                <FontAwesomeIcon icon={faInfoCircle} className="mr-2" size="xl"/>
              </div>

              <div className="flex-grow text-2xl font-semibold text-center px-4">
                {helpRefresh && "Допомога / Інструкція"}
                {requisiteRefresh && "Реквізити та оголошення"}
                {telegramRefresh && "Telegram бот"}
              </div>

              <div className="flex-none text-2xl font-semibold text-right">
                <Button onClick={closeHelp}>
                  <FontAwesomeIcon icon={faClose}/>
                </Button>
              </div>
            </div>
            {RenderHelp()}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div
        className={"w-full max-w-md bg-white rounded-2xl shadow-lg mb-4 p-4 items-center justify-center text-center"}>
        <Link to={"#"} onClick={toggleHelp} className={"p-2 flex justify-center items-center"}>
          <FontAwesomeIcon icon={faInfoCircle} className={"mr-2"} size={"2xl"}/>Допомога / Інструкція
        </Link>
      </div>
      <div
        className={"w-full max-w-md bg-white rounded-2xl shadow-lg mb-4 p-4 items-center justify-center text-center"}>
        <Link to={"#"} onClick={toggleRequisite} className={"p-2 flex justify-center items-center"}>
          <FontAwesomeIcon icon={faInfoCircle} className={"mr-2"} size={"2xl"}/>Реквізити та оголошення
        </Link>
      </div>
      {searchPopup ? (
        <div
          className="w-full max-w-md bg-white rounded-2xl shadow-lg min-h-[550px] border-2 border-indigo-500 mb-4">
          <div className="flex w-full">
            <span className="text-2xl font-bold text-right w-full bg-indigo-500
            text-white font-semibold py-2 rounded-t-xl transition flex justify-center items-center">Пошук Користувача</span>
            <button
              className="text-2xl font-bold text-right w-1/2 font-semibold py-2 rounded-t-xl transition flex justify-center items-center"
              onClick={() => setSearchPopup(prev => !prev)}
            >
              <span className="text-2xl font-bold">Увійти</span>
            </button>
          </div>

          <div
            className="border-t-2 border-indigo-500 pl-8 pr-8 pt-4">
            <label className="block mb-2 text-sm font-medium text-gray-600">Пошук Користувача</label>
            <div className="items-start grid grid-cols-1 gap-2">
              <span
                className="block ml-2 text-sm font-medium text-gray-600">Ім'я / Адреса / Номер Телефона / Код НС2</span>
              <div className="flex flex-col w-full">
                <input
                  className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="118 9"
                  autoComplete="search"
                />
              </div>
            </div>
            <label className="block m-2 mt-4 text-medium font-bold text-gray-600 ">Виберіть <FontAwesomeIcon
              icon={faArrowDown}/></label>
            <ul
              className="mt-4 max-h-60 overflow-y-auto border-2 border-indigo-500 min-h-[300px] rounded-xl p-2 pb-5">
              {Array.isArray(found) && found.length > 0 && Object.values(found).map((entry: Record<string, string>) => {
                if (!entry || typeof entry !== 'object') return null;
                const value = entry;
                try {
                  return (
                    <ErrorBoundary name={"LoginPage:foundUserItem"} key={"foundUserItem-" + (value?.["account"] || Math.random())}>
                      <li
                        key={value?.["account"]}
                        className="text-sm text-gray-700 mt-2 cursor-pointer hover:text-indigo-600"
                        onClick={() => {
                          setInput(value?.["account"] || "");
                          setSearchPopup(prev => false)
                        }}
                      >
                        {input === value?.["account"] ? (
                          <>
                            <strong className="text-green-600 font-semibold"><FontAwesomeIcon
                              icon={faChevronRight}/> {value?.["account"] || ""}
                            </strong><span> {value?.["address"] || ""}</span>
                          </>
                        ) : (
                          <>
                        <span
                          className="text-indigo-600 font-semibold">{value?.["account"] || ""}</span><span> {value?.["address"] || ""}</span>
                          </>
                        )}
                      </li>
                    </ErrorBoundary>
                  )
                } catch (error) {
                  return (
                    <li key={Math.random()} className="text-sm text-red-500 mt-2">
                      {error instanceof Error ? `${error.message}` : ''}
                    </li>
                  );
                }
              })}
            </ul>
          </div>
        </div>
      ) : (
        <ErrorBoundary name={"LoginPage:LoginForm"}>
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-lg min-h-[550px] border-2 border-indigo-500 mb-4">
            <div className="flex w-full">
              <button
                className="text-2xl font-bold text-right w-full font-semibold py-2 rounded-t-xl transition flex justify-center items-center"
                onClick={() => setSearchPopup(prev => !prev)}
              >
                <span className="md:text-2xl text-xl font-bold">Пошук Користувача</span>
              </button>
              <span className="md:text-2xl ftext-xl font-bold text-right w-1/2 bg-indigo-500
            text-white font-semibold py-2 rounded-t-xl transition flex justify-center items-center">Увійти</span>
            </div>

            <div
              className="border-t-2 border-indigo-500 pl-8 pr-8 pt-4">
              <label className="block mb-2 text-sm font-medium text-gray-600">Користувач</label>
              <input
                className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="+380-XX-XXX-XX-XX"
                autoComplete="username"
              />
              <label className="block mb-2 text-sm font-medium text-gray-600">Пароль</label>
              <input
                className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ваш пароль"
                onKeyPress={submitOnEnter}
                autoComplete="username"
              />

              {error && <p className="text-sm text-red-500 mt-2">{error}</p>}

              <button
                className="mt-6 w-full bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-2 rounded-xl transition"
                onClick={handleLogin}
              >
                Вхід
              </button>
            </div>
            <div
              className="pl-8 pr-8 pt-4 mb-4">
              <ul
                className="mt-4 max-h-60 overflow-y-auto border-2 border-indigo-500 min-h-[210px] rounded-xl p-2 pb-5">
                {Array.isArray(found) && found.length > 0 && Object.values(found).map((entry: Record<string, string>) => {
                  if (!entry || typeof entry !== 'object') return null;
                  const value = entry;
                  try {
                    return (
                      <ErrorBoundary name={"LoginPage:foundUserItem"} key={"foundUserItem-" + (value?.["account"] || Math.random())}>
                        <li
                          key={value?.["account"]}
                          className="text-sm text-gray-700 mt-2 cursor-pointer hover:text-indigo-600"
                          onClick={() => {
                            setInput(value?.["account"] || "");
                            setSearchPopup(prev => false)
                          }}
                        >
                          {input === value?.["account"] ? (
                            <>
                              <strong className="text-green-600 font-semibold"><FontAwesomeIcon
                                icon={faChevronRight}/> {value?.["account"]}</strong><span> {value?.["address"]}</span>
                            </>
                          ) : (
                            <>
                        <span
                          className="text-indigo-600 font-semibold">{value?.["account"]}</span><span> {value?.["address"]}</span>
                            </>
                          )}
                        </li>
                      </ErrorBoundary>
                    )
                  } catch (error) {
                    return (
                      <li key={Math.random()} className="text-sm text-red-500 mt-2">
                        {error instanceof Error ? `${error.message}` : ''}
                      </li>
                    );
                  }
                })}
              </ul>
            </div>
          </div>
        </ErrorBoundary>
      )}
      <div
        className={"w-full max-w-md bg-white rounded-2xl shadow-lg mb-4 p-4 items-center justify-center text-center"}>
        <Link to={"#"} onClick={toggleTelegram} className={"p-2 flex justify-center items-center"}>
          <TelegramIcon width="20" height="20" className={"mr-1"}/>Telegram бот
        </Link>
      </div>
    </div>
  )
    ;
}
